/**
 * Streaming response types for ConvoAI
 */

export function deriveHost(): string {
	const {
		origin = '',
		port = '',
		hostname = '',
	} = typeof window.location !== 'undefined' ? window.location : {};

	if (
		(hostname === 'localhost' && port !== '8081') ||
		[
			'atlaskit-website-staging.stg-east.frontend.public.atl-paas.net',
			'statlas.prod.atl-paas.net',
			'atlaskit.atlassian.com',
		].some((h) => hostname === h)
	) {
		return 'https://pug.jira-dev.com';
	}

	return origin;
}

export type StreamEventType = 'TRACE' | 'ANSWER_PART' | 'FINAL_RESPONSE' | 'ERROR';

export interface StreamEvent {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	[key: string]: any;
	message?: {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		[key: string]: any;
		content?: string;
		message_template?: string;
		status_code?: number;
	};
	type: StreamEventType;
}

export interface StreamingOptions {
	/**
	 * Callback invoked for each parsed stream chunk
	 */
	onChunk?: (data: StreamEvent) => void;
	/**
	 * Callback invoked on error
	 */
	onError?: (error: Error) => void;
	/**
	 * Callback invoked when FINAL_RESPONSE is received
	 */
	onFinalResponse?: (data: StreamEvent) => void;
	/**
	 * AbortSignal to cancel the streaming request
	 */
	signal?: AbortSignal;
}

export interface StreamingResult<T = StreamEvent> {
	chunks: StreamEvent[];
	finalResponse: T | null;
}

/**
 * Reads a streaming response body and parses each line as JSON.
 * Collects all chunks and returns the final response.
 *
 * @param body - The ReadableStream from the fetch response
 * @param options - Optional callbacks and configuration
 * @returns Promise resolving to the streaming result with final response and all chunks
 */
export async function readStreamingResponse<T = StreamEvent>(
	body: ReadableStream<Uint8Array>,
	options: StreamingOptions = {},
): Promise<StreamingResult<T>> {
	const { onChunk, onFinalResponse, onError, signal } = options;

	const reader = body.getReader();
	const decoder = new TextDecoder('utf-8');
	let buffer = '';
	let finalResponse: T | null = null;
	const chunks: StreamEvent[] = [];

	try {
		while (!signal?.aborted) {
			const { value, done } = await reader.read();
			if (done) {
				break;
			}

			const chunk = decoder.decode(value, { stream: true });
			buffer += chunk;

			// Split by newlines and process complete lines
			const lines = buffer.split('\n');
			// Keep the last potentially incomplete line in the buffer
			buffer = lines.pop() || '';

			for (const line of lines) {
				if (!line.trim()) {
					continue;
				}

				try {
					const data = JSON.parse(line) as StreamEvent;
					chunks.push(data);
					onChunk?.(data);

					// Check for FINAL_RESPONSE type (ConvoAI pattern)
					if (data.type === 'FINAL_RESPONSE') {
						finalResponse = data as T;
						onFinalResponse?.(data);
					}
				} catch (parseError) {
					const error = new Error(
						`Failed to parse JSON line: ${parseError instanceof Error ? parseError.message : String(parseError)}`,
					);
					onError?.(error);
				}
			}
		}

		// Check if aborted
		if (signal?.aborted) {
			reader.cancel();
		}

		// Process any remaining buffer content
		if (buffer.trim()) {
			try {
				const data = JSON.parse(buffer) as StreamEvent;
				chunks.push(data);
				onChunk?.(data);

				if (data.type === 'FINAL_RESPONSE') {
					finalResponse = data as T;
					onFinalResponse?.(data);
				}
			} catch {
				// Ignore incomplete final chunk
			}
		}
	} catch (error) {
		const streamError = error instanceof Error ? error : new Error(String(error));
		onError?.(streamError);
		throw streamError;
	}

	return { finalResponse, chunks };
}

export interface FetchStreamingOptions extends StreamingOptions {
	/**
	 * Fetch request options (method, headers, body, etc.)
	 */
	fetchOptions?: RequestInit;
	/**
	 * Fetch request URL
	 */
	url: string;
}

/**
 * Performs a fetch request and reads the streaming response.
 * Convenience wrapper around readStreamingResponse.
 *
 * @param options - URL, fetch options, and streaming callbacks
 * @returns Promise resolving to the streaming result
 */
export async function fetchStreaming<T = StreamEvent>(
	options: FetchStreamingOptions,
): Promise<StreamingResult<T>> {
	const { url, fetchOptions = {}, signal, onChunk, onError, onFinalResponse } = options;

	const response = await fetch(url, {
		method: 'POST',
		credentials: 'include',
		mode: 'cors',
		headers: {
			'Content-Type': 'application/json;charset=UTF-8',
			'X-Channel-Id': 'editor',
			'X-Product': 'confluence',
			'X-Experience-Id': 'editor',
		},
		...fetchOptions,
		signal,
	});

	if (!response.ok) {
		const error = new Error(`Response not OK: ${response.status}`);
		onError?.(error);
		throw error;
	}

	if (!response.body) {
		const error = new Error('No response body');
		onError?.(error);
		throw error;
	}

	return readStreamingResponse<T>(response.body, { onChunk, onError, onFinalResponse, signal });
}
